import type { ScheduledCase, SurgeonTemplate } from '@/lib/templates/types';

interface GetRelevantProcedureTemplatesParams {
  templates: SurgeonTemplate[];
  selectedCase: ScheduledCase;
  selectedTemplate: SurgeonTemplate | null;
}

function normalizeText(value: string | null | undefined): string {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function primaryCodes(template: SurgeonTemplate | null | undefined): Set<string> {
  return new Set((template?.primary_cpts || []).map(cpt => String(cpt.code || '').trim().toUpperCase()).filter(Boolean));
}

function hasPrimaryCodeOverlap(template: SurgeonTemplate, selectedCodes: Set<string>): boolean {
  if (selectedCodes.size === 0) return false;
  return (template.primary_cpts || []).some(cpt => selectedCodes.has(String(cpt.code || '').trim().toUpperCase()));
}

function hasStrongProcedureNameMatch(template: SurgeonTemplate, scheduledProcedure: string): boolean {
  const templateName = normalizeText(template.template_name);
  const procedureName = normalizeText(scheduledProcedure);
  if (!templateName || !procedureName) return false;
  return templateName === procedureName || procedureName.includes(templateName) || templateName.includes(procedureName);
}

export function getRelevantProcedureTemplates({
  templates,
  selectedCase,
  selectedTemplate,
}: GetRelevantProcedureTemplatesParams): SurgeonTemplate[] {
  if (templates.length <= 1) return templates;

  const selectedPrimaryCodes = primaryCodes(selectedTemplate);
  const filtered = templates.filter(template => (
    hasPrimaryCodeOverlap(template, selectedPrimaryCodes) ||
    hasStrongProcedureNameMatch(template, selectedCase.scheduled_procedure || '') ||
    (selectedTemplate ? normalizeText(template.template_name) === normalizeText(selectedTemplate.template_name) : false)
  ));

  if (filtered.length > 0) return filtered;
  if (selectedTemplate) return [selectedTemplate];
  return templates;
}
